/**
 * Seed packets: photograph the pile and Grow adds every plant with its dates
 * for the visitor's ZIP. The evidence leads: the photo of the pile, and the
 * plants it added, each with its own packet. Beside them, the gardener's pain
 * about typing every plant in, the heading, the lead ("From chaos to
 * controlled."), the body and a third line, "See what you can plant right
 * now" (landing.packets.plantNow*).
 *
 * The photographs are the owner's own scan of their bed "Side garden": the
 * pile of packets, and three of the six packets Grow cut out of it. Their
 * addresses are in LANDING_PACKET_PHOTOS (config/tunables/landing), one per
 * slot; a slot with none is a plain framed box (PhotoSlot, shown in
 * production too, by the owner's choice). The example list names the plants
 * those packets are.
 *
 * Words (en/es): landing.packets.*, landing.slots.*.
 *
 * @module SeedPackets
 */

import React from 'react';
import { Package } from 'lucide-react';
import { LANDING_PACKET_PHOTOS } from '@/config/tunables/landing';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import { PhotoSlot } from '../components/PhotoSlot';

/** One example plant: its name and dates. */
interface Example {
  /** The row's key, and its slot in LANDING_PACKET_PHOTOS. */
  id: 'packet1' | 'packet2' | 'packet3';
  /** The plant's name (a translation key). */
  name: string;
  /** Its dates (a translation key). */
  dates: string;
}

/** The packets from the example photo that the list shows (three of its six). */
const EXAMPLES: readonly Example[] = [
  { id: 'packet1', name: 'landing.packets.items.packet1.name', dates: 'landing.packets.items.packet1.dates' },
  { id: 'packet2', name: 'landing.packets.items.packet2.name', dates: 'landing.packets.items.packet2.dates' },
  { id: 'packet3', name: 'landing.packets.items.packet3.name', dates: 'landing.packets.items.packet3.dates' },
];

/** The seed packets section: the pain, the pile photo, and the plants it adds with their packet photos. */
export const SeedPackets: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  return (
    <FeatureBlock
      icon={<Package className="h-8 w-8" strokeWidth={1.75} />}
      pain={t('landing.packets.pain')}
      title={t('landing.packets.title')}
      line={<b>{t('landing.packets.lead')}</b>}
      second={t('landing.packets.body')}
      third={<><b>{t('landing.packets.plantNowBold')}</b>{t('landing.packets.plantNowRest')}</>}
      evidence={(
        <>
          <PhotoSlot label={t('landing.slots.pile')} className="aspect-[4/5] w-full rounded-2xl object-center shadow-xl" src={LANDING_PACKET_PHOTOS.pile} alwaysShown />
          <div className="space-y-3 rounded-2xl border-2 border-primary bg-card p-4 text-left shadow-xl">
            <p className="font-grow text-base font-bold">{t('landing.packets.listTitle')}</p>
            {EXAMPLES.map((example) => (
              <div key={example.id} className="flex items-center gap-3">
                <PhotoSlot label={t(`landing.slots.${example.id}`)} className="h-16 w-12 shrink-0 p-1 text-[10px] leading-tight" src={LANDING_PACKET_PHOTOS[example.id]} alwaysShown />
                <p>
                  <b>{t(example.name)}</b>
                  <br />
                  {t(example.dates)}
                </p>
              </div>
            ))}
            <p className="text-sm text-muted-foreground">{t('landing.packets.more')}</p>
          </div>
        </>
      )}
    />
  );
};
